import { supabase } from "@/integrations/supabase/client";
import type { DataAPI } from "./types";
import { buildAdminInsights } from "./insights";
import type { EmailScenarioId } from "./types";
import { isEmailScenarioEnabled } from "@/os/email/emailScenarios";
import {
  applyClientFilter,
  applyProjectFilter,
  applyInvoiceFilter,
  applyQuoteFilter,
} from "@/os/settings/dataFilterApply";

/** Fetch minimal client rows so list filters can decide simulated vs real. */
async function fetchClientFlags(): Promise<Array<{ id: string; is_simulated: boolean | null; created_at: string | null }>> {
  const { data } = await supabase.from("clients").select("id,is_simulated,created_at");
  return (data ?? []) as any;
}

/**
 * Bulk-fetch the active project team for a set of project ids and group it
 * into a Map<projectId, members[]> (sorted by order_index, capped to 6).
 * Returns an empty map if `projectIds` is empty.
 */
async function fetchTeamMembersByProject(
  projectIds: string[],
): Promise<Map<string, Array<{ id: string; display_name: string; avatar_url: string | null; role_on_project: string | null }>>> {
  const map = new Map<string, Array<{ id: string; display_name: string; avatar_url: string | null; role_on_project: string | null }>>();
  if (projectIds.length === 0) return map;
  const { data } = await supabase
    .from("project_team")
    .select("project_id, role_on_project, order_index, ended_at, team_profile_id, team_profiles!inner(id, display_name, avatar_url)")
    .in("project_id", projectIds)
    .is("ended_at", null)
    .order("order_index", { ascending: true });
  for (const row of (data ?? []) as any[]) {
    const tp = row.team_profiles;
    if (!tp) continue;
    const arr = map.get(row.project_id) ?? [];
    if (arr.length >= 6) continue;
    arr.push({
      id: tp.id,
      display_name: tp.display_name,
      avatar_url: tp.avatar_url ?? null,
      role_on_project: row.role_on_project ?? null,
    });
    map.set(row.project_id, arr);
  }
  return map;
}

/** Fire-and-forget transactional email. Never throws.
 * Gated by Settings → Emails: if the scenario is disabled, the send is skipped. */
async function sendTxEmail(
  scenarioId: EmailScenarioId,
  templateName: string,
  recipientEmail: string,
  idempotencyKey: string,
  templateData: Record<string, any> = {},
) {
  try {
    // Lightweight settings lookup — gated by RLS but read-only and small.
    const { data } = await supabase
      .from("app_settings" as any)
      .select("email_settings")
      .eq("id", true)
      .maybeSingle();
    const emailSettings = (data as any)?.email_settings ?? {};
    if (!isEmailScenarioEnabled(emailSettings, scenarioId)) {
      console.info("[email] scenario disabled, skipping", scenarioId);
      return;
    }
    await supabase.functions.invoke("send-transactional-email", {
      body: { templateName, recipientEmail, idempotencyKey, templateData },
    });
  } catch (err) {
    console.warn("[email] send failed", templateName, err);
  }
}

/**
 * Lovable Cloud (Supabase) data adapter for the Production environment.
 * Mirrors the queries that used to live inline in each OS page.
 */
export const cloudApi: DataAPI = {
  async adminDashboardStats() {
    const [allClients, allProjects, allInvoices, allQuotes] = await Promise.all([
      cloudApi.listClients(),
      supabase
        .from("projects")
        .select("id,client_id,status,created_at,start_date,due_date")
        .neq("status", "archived")
        .then((r) => (r.data ?? []) as any[]),
      supabase
        .from("invoices")
        .select("amount,status,due_date,paid_at,client_id,issue_date,created_at")
        .then((r) => (r.data ?? []) as any[]),
      supabase
        .from("quotes")
        .select("id,client_id,created_at,valid_until")
        .then((r) => (r.data ?? []) as any[]),
    ]);
    const projects = applyProjectFilter(allProjects, allClients as any);
    const invoices = applyInvoiceFilter(allInvoices, allClients as any);
    void applyQuoteFilter(allQuotes, allClients as any); // keep parity with mock
    const now = new Date();
    const openInvoices = invoices.filter((i: any) => i.status !== "paid" && i.status !== "void").length;
    const overdue = invoices.filter((i: any) => i.status !== "paid" && i.status !== "void" && i.due_date && new Date(i.due_date) < now).length;
    const revenue = invoices.filter((i: any) => i.status === "paid").reduce((s: number, i: any) => s + Number(i.amount ?? 0), 0);
    return { clients: (allClients as any[]).length, activeProjects: projects.length, openInvoices, overdue, revenue };
  },

  async listClients() {
    const [{ data: clients }, { data: projects }, { data: invoices }] = await Promise.all([
      supabase.from("clients")
        .select("id, company_name, contact_name, contact_email, contact_phone, status, notes, user_id, created_at, preferred_language, default_payment_method_id, is_simulated, client_type")
        .order("created_at", { ascending: false }),
      supabase.from("projects").select("client_id,status,updated_at"),
      supabase.from("invoices").select("client_id,amount,status,paid_at,created_at"),
    ]);
    const projByClient: Record<string, any[]> = {};
    (projects ?? []).forEach((p: any) => { if (p.client_id) (projByClient[p.client_id] ||= []).push(p); });
    const invByClient: Record<string, any[]> = {};
    (invoices ?? []).forEach((i: any) => { if (i.client_id) (invByClient[i.client_id] ||= []).push(i); });
    const enriched = (clients ?? []).map((c: any) => {
      const ps = projByClient[c.id] ?? [];
      const is = invByClient[c.id] ?? [];
      const billed_total = is.filter((i: any) => i.status === "paid").reduce((s: number, i: any) => s + Number(i.amount ?? 0), 0);
      const outstanding = is.filter((i: any) => i.status !== "paid" && i.status !== "void").reduce((s: number, i: any) => s + Number(i.amount ?? 0), 0);
      const times = [c.created_at, ...ps.map((p: any) => p.updated_at), ...is.map((i: any) => i.paid_at ?? i.created_at)].filter(Boolean) as string[];
      const last_activity = times.length ? times.sort().slice(-1)[0] : c.created_at;
      return { ...c, projects_count: ps.filter((p: any) => p.status !== "archived").length, billed_total, outstanding, last_activity };
    });
    return applyClientFilter(enriched as any, { projects: (projects ?? []) as any, invoices: (invoices ?? []) as any }) as any;
  },
  async listClientsLite() {
    const { data } = await supabase.from("clients").select("id,company_name,preferred_language,is_simulated,created_at,client_type").order("company_name");
    return applyClientFilter((data ?? []) as any) as any;
  },
  async getClient(id) {
    const { data: client } = await supabase.from("clients").select("*").eq("id", id).maybeSingle();
    const { data: projects } = await supabase.from("projects").select("id,name,status,progress").eq("client_id", id);
    const { data: invoices } = await supabase.from("invoices").select("id,invoice_number,amount,currency,status,due_date").eq("client_id", id);
    return { client: client as any, projects: (projects ?? []) as any, invoices: (invoices ?? []) as any };
  },
  async createClient(p) {
    const { data, error } = await supabase.from("clients").insert(p as any).select("id").single();
    if (error) throw error;
    return (data as any).id as string;
  },
  async updateClient(id, patch) {
    const { error } = await supabase.from("clients").update(patch as any).eq("id", id);
    if (error) throw error;
  },

  async getClientImpact(id) {
    const [{ data: projects }, { data: quotes }, { data: invoices }] = await Promise.all([
      supabase.from("projects").select("id,status,currency").eq("client_id", id),
      supabase.from("quotes").select("id,status").eq("client_id", id),
      supabase.from("invoices").select("id,amount,status,currency").eq("client_id", id),
    ]);
    const activeProjects = (projects ?? []).filter((p: any) => p.status !== "archived").length;
    const openQuotes = (quotes ?? []).filter((q: any) => ["draft", "sent", "accepted"].includes(q.status)).length;
    const outstandingAmount = (invoices ?? [])
      .filter((i: any) => i.status !== "paid" && i.status !== "void")
      .reduce((s: number, i: any) => s + Number(i.amount ?? 0), 0);
    const invoiceIds = (invoices ?? []).map((i: any) => i.id);
    let pendingPaymentsCount = 0;
    if (invoiceIds.length) {
      const { data: pays } = await supabase
        .from("payments")
        .select("id,status,invoice_id")
        .in("invoice_id", invoiceIds)
        .eq("status", "declared");
      pendingPaymentsCount = (pays ?? []).length;
    }
    const currency = (projects?.[0] as any)?.currency ?? (invoices?.[0] as any)?.currency ?? "USD";
    return { activeProjects, openQuotes, outstandingAmount, pendingPaymentsCount, currency };
  },

  async setClientActive(id, active) {
    const nextStatus = active ? "active" : "inactive";
    const { error: cErr } = await supabase.from("clients").update({ status: nextStatus } as any).eq("id", id);
    if (cErr) throw cErr;
    if (active) return;
    // Cascade on deactivation.
    const { data: projects } = await supabase.from("projects").select("id,billing,status").eq("client_id", id);
    const pausedAt = new Date().toISOString();
    await Promise.all(
      (projects ?? []).map(async (p: any) => {
        const billing = { ...(p.billing ?? {}), paused_at: pausedAt, paused_reason: "client_inactive" };
        await supabase.from("projects")
          .update({ status: p.status === "archived" ? p.status : "archived", billing } as any)
          .eq("id", p.id);
      })
    );
    await supabase
      .from("quotes")
      .update({ status: "rejected" } as any)
      .eq("client_id", id)
      .in("status", ["draft", "sent", "accepted"]);
  },

  async listProjects() {
    const [{ data }, { data: ms }, { data: ts }, { data: mb }, { data: ups }] = await Promise.all([
      supabase.from("projects")
        .select("id,name,description,status,progress,budget,currency,client_id,start_date,due_date,created_at,billing,clients(company_name)")
        .order("created_at", { ascending: false }),
      supabase.from("milestones").select("project_id,status"),
      supabase.from("tasks").select("project_id,status"),
      supabase.from("project_members").select("project_id,user_id"),
      supabase.from("project_updates").select("project_id,created_at"),
    ]);
    const msBy: Record<string, any[]> = {};
    (ms ?? []).forEach((m: any) => (msBy[m.project_id] ||= []).push(m));
    const tsBy: Record<string, any[]> = {};
    (ts ?? []).forEach((t: any) => (tsBy[t.project_id] ||= []).push(t));
    const mbBy: Record<string, Set<string>> = {};
    (mb ?? []).forEach((m: any) => ((mbBy[m.project_id] ||= new Set())).add(m.user_id));
    const upBy: Record<string, string[]> = {};
    (ups ?? []).forEach((u: any) => (upBy[u.project_id] ||= []).push(u.created_at));
    const teamBy = await fetchTeamMembersByProject((data ?? []).map((p: any) => p.id));
    const enriched = (data ?? []).map((p: any) => {
      const milestones = msBy[p.id] ?? [];
      const tasks = tsBy[p.id] ?? [];
      const updates = (upBy[p.id] ?? []).sort();
      const team = teamBy.get(p.id) ?? [];
      return {
        ...p,
        team_count: Math.max(mbBy[p.id]?.size ?? 0, team.length),
        team_members: team,
        open_tasks: tasks.filter((t: any) => t.status !== "done" && t.status !== "cancelled").length,
        milestones_total: milestones.length,
        milestones_done: milestones.filter((m: any) => m.status === "completed" || m.status === "done").length,
        last_update: updates.length ? updates[updates.length - 1] : null,
      };
    });
    const flags = await fetchClientFlags();
    return applyProjectFilter(enriched as any, flags as any) as any;
  },
  async createProject(p) {
    // If the caller didn't pass a snapshot, lock in the current global
    // discount so the project's price guarantee can't drift later.
    let payload: any = { ...p };
    if (payload.resources_discount_percent_snapshot == null) {
      const { data: s } = await supabase
        .from("app_settings")
        .select("resources_discount_percent")
        .maybeSingle();
      payload.resources_discount_percent_snapshot = Number((s as any)?.resources_discount_percent ?? 0);
    }
    const { error } = await supabase.from("projects").insert(payload as any);
    if (error) throw error;
  },
  async getProjectDetail(id) {
    const [{ data: project }, { data: milestones }, { data: tasks }, { data: members }, { data: updates }] = await Promise.all([
      supabase.from("projects")
        .select("id,name,description,status,progress,budget,currency,client_id,start_date,due_date,created_at,resources_discount_percent_snapshot,billing,clients(company_name)")
        .eq("id", id)
        .maybeSingle(),
      supabase.from("milestones").select("*").eq("project_id", id).order("order_index"),
      supabase.from("tasks").select("*").eq("project_id", id).order("created_at"),
      supabase.from("project_members").select("user_id, project_id, role, profiles:user_id(full_name, email)").eq("project_id", id),
      supabase.from("project_updates").select("*").eq("project_id", id).order("created_at", { ascending: false }),
    ]);
    return {
      project: project as any,
      milestones: (milestones ?? []) as any,
      tasks: (tasks ?? []) as any,
      members: (members ?? []) as any,
      updates: (updates ?? []) as any,
    };
  },
  async updateProject(id, patch) {
    const { error } = await supabase.from("projects").update(patch as any).eq("id", id);
    if (error) throw error;
  },
  async getProjectAISettings(id) {
    const [{ data: project, error: pe }, { data: ai, error: ae }] = await Promise.all([
      supabase.from("projects")
        .select("id,name,status,clients(company_name)")
        .eq("id", id)
        .maybeSingle(),
      (supabase as any).rpc("get_project_ai_settings", { _project_id: id }),
    ]);
    if (pe) throw pe;
    if (ae) throw ae;
    return { project: project ? { ...(project as any), ai_settings: ai ?? {} } : null } as any;
  },
  async updateProjectAISettings(id, settings) {
    const { error } = await (supabase as any).rpc("update_project_ai_settings", {
      _project_id: id,
      _settings: settings ?? {},
    });
    if (error) throw error;
  },
  async createMilestone(p) {
    const { error } = await supabase.from("milestones").insert(p as any);
    if (error) throw error;
  },
  async createTask(p) {
    const { error } = await supabase.from("tasks").insert(p as any);
    if (error) throw error;
  },
  async createProjectUpdate(p) {
    const id = (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
    const { error } = await supabase.from("project_updates").insert({ ...(p as any), id });
    if (error) throw error;
    // Best-effort: notify the project's primary client contact.
    try {
      const { data: proj } = await supabase
        .from("projects")
        .select("name, clients(contact_name, contact_email)")
        .eq("id", p.project_id)
        .maybeSingle();
      const c: any = (proj as any)?.clients;
      const email = c?.contact_email as string | null;
      if (email) {
        const firstName = (c?.contact_name as string | null)?.split(/\s+/)[0] ?? null;
        const origin = typeof window !== "undefined" ? window.location.origin : "https://smart-canvas.com";
        await sendTxEmail("project-update", "project-update", email, `project-update-${id}`, {
          clientName: firstName,
          projectName: (proj as any)?.name ?? "tu proyecto",
          updateTitle: p.title,
          updateBody: p.body ?? "",
          ctaUrl: `${origin}/os/client/projects/${p.project_id}`,
        });
      }
    } catch (err) {
      console.warn("[email] project-update lookup failed", err);
    }
  },
  async listOperatorsForAssign() {
    const { data } = await supabase.from("user_roles").select("user_id, profiles:user_id(full_name,email)").eq("role", "operator");
    return (data ?? []) as any;
  },
  async assignProjectMember(project_id, user_id) {
    const { error } = await supabase.from("project_members").insert({ project_id, user_id } as any);
    if (error) throw error;
  },

  async listInvoices() {
    const [{ data }, flags] = await Promise.all([
      supabase.from("invoices").select("*, clients(company_name)").order("created_at", { ascending: false }),
      fetchClientFlags(),
    ]);
    return applyInvoiceFilter((data ?? []) as any, flags as any) as any;
  },
  async createInvoice(p) {
    const { data, error } = await supabase.from("invoices").insert(p as any).select("id").single();
    if (error) throw error;
    return (data as any).id as string;
  },
  async markInvoicePaid(id) {
    const { error } = await supabase.from("invoices").update({ status: "paid", paid_at: new Date().toISOString() } as any).eq("id", id);
    if (error) throw error;
  },
  async updateInvoice(id, patch) {
    const { error } = await supabase.from("invoices").update(patch as any).eq("id", id);
    if (error) throw error;
  },
  async deleteInvoice(id) {
    // Best-effort: detach any storage objects associated with the invoice and its payments.
    const { data: inv } = await supabase.from("invoices").select("attachment_path").eq("id", id).maybeSingle();
    const { data: pays } = await supabase.from("payments").select("receipt_path").eq("invoice_id", id);
    const attachPaths = [inv?.attachment_path].filter(Boolean) as string[];
    const receiptPaths = (pays ?? []).map((p: any) => p.receipt_path).filter(Boolean) as string[];
    if (attachPaths.length) await supabase.storage.from("invoice-attachments").remove(attachPaths);
    if (receiptPaths.length) await supabase.storage.from("payment-receipts").remove(receiptPaths);
    // payments cascade is not configured; delete manually
    await supabase.from("payments").delete().eq("invoice_id", id);
    const { error } = await supabase.from("invoices").delete().eq("id", id);
    if (error) throw error;
  },
  async listInvoicePayments(invoiceId) {
    const { data } = await supabase.from("payments").select("*").eq("invoice_id", invoiceId).order("paid_at", { ascending: false });
    return (data ?? []) as any;
  },
  async updatePayment(id, patch) {
    const { error } = await supabase.from("payments").update(patch as any).eq("id", id);
    if (error) throw error;
  },
  async deletePayment(id) {
    const { data: pay } = await supabase.from("payments").select("receipt_path").eq("id", id).maybeSingle();
    if (pay?.receipt_path) {
      await supabase.storage.from("payment-receipts").remove([pay.receipt_path]);
    }
    const { error } = await supabase.from("payments").delete().eq("id", id);
    if (error) throw error;
  },
  async listInvoiceReminders(invoiceId) {
    const { data } = await supabase
      .from("invoice_reminders" as any)
      .select("*")
      .eq("invoice_id", invoiceId)
      .order("sent_at", { ascending: false });
    return (data ?? []) as any;
  },
  async createInvoiceReminder(p) {
    const { error } = await supabase.from("invoice_reminders" as any).insert(p as any);
    if (error) throw error;
    await supabase.from("invoices").update({ last_reminder_at: new Date().toISOString() } as any).eq("id", p.invoice_id);
  },

  async listQuotes() {
    const [{ data }, flags] = await Promise.all([
      supabase
        .from("quotes")
        .select("*, clients(company_name, contact_name, contact_email, user_id, status)")
        .eq("is_active_version", true)
        .order("created_at", { ascending: false }),
      fetchClientFlags(),
    ]);
    return applyQuoteFilter((data ?? []) as any, flags as any) as any;
  },
  async createQuote(p) {
    const { data, error } = await supabase.from("quotes").insert(p as any).select("id").single();
    if (error) throw error;
    return (data as any).id as string;
  },
  async updateQuote(id, patch) {
    const p: any = { ...(patch as any) };
    const FROZEN = new Set(["sent", "accepted"]);
    // Capture prior status to know whether this is a true transition (avoid
    // re-sending the client email when an already-sent quote is edited).
    let priorStatus: string | null = null;
    let quoteSnapshot: any = null;
    if (p.status && FROZEN.has(p.status)) {
      // Snapshot the global + per-quote discount when sending/accepting so the
      // quote's totals stop floating with the live `resources_discount_percent`.
      const { data: cur } = await supabase
        .from("quotes")
        .select("status, discount_snapshot, ai_payload, title, quote_number, client_id, clients(contact_name, contact_email, preferred_language, company_name, user_id, status)")
        .eq("id", id)
        .maybeSingle();
      priorStatus = (cur as any)?.status ?? null;
      quoteSnapshot = cur;
      const alreadyFrozen = (cur as any)?.discount_snapshot != null;
      if (!alreadyFrozen) {
        const { data: settings } = await supabase
          .from("app_settings")
          .select("resources_discount_percent")
          .maybeSingle();
        const ai = (p.ai_payload ?? (cur as any)?.ai_payload) as any;
        p.discount_snapshot = {
          percent: Number((settings as any)?.resources_discount_percent ?? 0),
          per_quote_percent: Number(ai?.discount?.percent ?? 0),
          captured_at: new Date().toISOString(),
          on_status: p.status,
        };
      }
    }
    // When sending a quote for the first time: if the linked client has no
    // platform user yet (typical for prospects), create the auth user + role
    // and fire the OS welcome email BEFORE flipping the quote to "sent".
    // If this fails, we abort so the invariant "sent ⇒ recipient has an
    // account" holds.
    if (
      p.status === "sent" &&
      priorStatus !== "sent" &&
      quoteSnapshot?.client_id &&
      !(quoteSnapshot as any)?.clients?.user_id
    ) {
      const cl: any = (quoteSnapshot as any).clients ?? {};
      const clientStatus = (cl.status as string | null) ?? null;
      const isProspect = clientStatus === "prospect";
      const kind: "prospect" | "client" = isProspect ? "prospect" : "client";
      const role: "prospect" | "client" = isProspect ? "prospect" : "client";
      const lang: "es" | "en" = cl.preferred_language === "en" ? "en" : "es";
      const origin =
        typeof window !== "undefined" ? window.location.origin : "https://smart-canvas.com";
      const { data: invRes, error: invErr } = await supabase.functions.invoke(
        "os-invite-user",
        {
          body: {
            kind,
            entity_id: quoteSnapshot.client_id,
            role,
            site_url: origin,
            lang,
          },
        },
      );
      if (invErr || (invRes as any)?.error) {
        const detail = (invRes as any)?.detail || (invRes as any)?.error || invErr?.message;
        throw new Error(
          `No pudimos crear el usuario para el destinatario de la cotización (${detail ?? "error desconocido"}). El cambio a "Enviado" se canceló.`,
        );
      }
      // Pre-warm the per-client AI briefs so login is instant.
      try {
        const { pregenerateClientBriefs } = await import("@/os/hooks/usePregenerateClientBriefs");
        // Pre-warm both ES and EN so the client never sees a loading state
        // regardless of the language they pick at login.
        void pregenerateClientBriefs(quoteSnapshot.client_id as string, ["es", "en"]);
      } catch (err) {
        console.warn("[pregenerateClientBriefs] skipped after invite", err);
      }
    } else if (
      p.status === "sent" &&
      priorStatus !== "sent" &&
      (quoteSnapshot as any)?.clients?.user_id
    ) {
      // The client already has a platform account (e.g. they were invited via
      // a previous flow), so os-invite-user is skipped. Still fire the OS
      // welcome email here — the idempotency key dedupes against any prior
      // send, so recipients who never got one will finally receive it.
      try {
        const cl: any = (quoteSnapshot as any).clients ?? {};
        if (cl.contact_email) {
          const lang: "es" | "en" = cl.preferred_language === "en" ? "en" : "es";
          const role: "prospect" | "client" =
            cl.status === "prospect" ? "prospect" : "client";
          const roleLabel = lang === "es"
            ? (role === "client" ? "Cliente" : "Prospecto")
            : (role === "client" ? "Client" : "Prospect");
          const origin =
            typeof window !== "undefined"
              ? window.location.origin
              : "https://smart-canvas.com";
          await sendTxEmail(
            "os-welcome",
            "os-welcome",
            String(cl.contact_email).trim(),
            `os-welcome-${cl.user_id}-${role}`,
            {
              inviteeName: cl.contact_name ?? cl.company_name ?? null,
              roleLabel,
              inviterName: null,
              loginUrl: `${origin}/os/login`,
              lang,
            },
          );
        }
      } catch (err) {
        console.warn("[email] os-welcome (existing user) failed", err);
      }
    }
    // When transitioning to "sent", atomically stamp sent_at and (if missing)
    // an open_token in the SAME update so we never end up with partial state
    // (status=sent without sent_at / open_token) if a later write fails.
    const isSendTransition =
      p.status === "sent" && priorStatus !== "sent" && !!quoteSnapshot;
    let prewarmOpenToken: string | null = null;
    if (isSendTransition) {
      (p as any).sent_at = new Date().toISOString();
      const existingToken = (quoteSnapshot as any)?.open_token ?? null;
      if (!existingToken) {
        prewarmOpenToken = crypto.randomUUID().replace(/-/g, "");
        (p as any).open_token = prewarmOpenToken;
      } else {
        prewarmOpenToken = existingToken;
      }
    }
    const { error } = await supabase.from("quotes").update(p).eq("id", id);
    if (error) throw error;
    // Fire client notification when transitioning to "sent" (and only once).
    if (isSendTransition) {
      try {
        const c: any = (quoteSnapshot as any).clients;
        const email = c?.contact_email as string | null;
        if (email) {
          const firstName = ((c?.contact_name as string | null) ?? "").split(/\s+/)[0] || null;
          const lang = (c?.preferred_language === "en" ? "en" : "es") as "es" | "en";
          const origin = typeof window !== "undefined" ? window.location.origin : "https://smart-canvas.com";
          const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL ?? "";
          const openToken = prewarmOpenToken;
          const pixelUrl = supabaseUrl
            ? `${supabaseUrl}/functions/v1/track-quote-open?q=${id}&t=${openToken}`
            : undefined;
          await sendTxEmail(
            "quote-sent-to-client",
            "quote-sent-to-client",
            email,
            `quote-sent-to-client-${id}`,
            {
              language: lang,
              recipientName: firstName,
              clientName: c?.company_name ?? null,
              quoteTitle: (quoteSnapshot as any).title ?? null,
              quoteNumber: (quoteSnapshot as any).quote_number ?? null,
              sentAt: new Date().toISOString().slice(0, 10),
              ctaUrl: `${origin}/q/${id}`,
              pixelUrl,
            },
          );
        }
      } catch (err) {
        console.warn("[email] quote-sent-to-client failed", err);
      }
    }
    // Fire admin notification when a quote transitions to "accepted".
    if (p.status === "accepted" && priorStatus !== "accepted" && quoteSnapshot) {
      // Auto-promote linked prospect → active and swap roles (prospect → client).
      try {
        const c: any = (quoteSnapshot as any).clients;
        const clientId = (quoteSnapshot as any).client_id as string | null;
        if (clientId && c?.status === "prospect") {
          await supabase
            .from("clients")
            .update({ status: "active" } as any)
            .eq("id", clientId);
          if (c?.user_id) {
            // Ensure client role is present (idempotent).
            await supabase
              .from("user_roles")
              .upsert({ user_id: c.user_id, role: "client" } as any, { onConflict: "user_id,role" });
            // Remove the obsolete prospect role.
            await supabase
              .from("user_roles")
              .delete()
              .eq("user_id", c.user_id)
              .eq("role", "prospect");
          }
        }
      } catch (err) {
        console.warn("[quote] auto-promote prospect failed", err);
      }
      try {
        const c: any = (quoteSnapshot as any).clients;
        const lang = (c?.preferred_language === "en" ? "en" : "es") as "es" | "en";
        const { data: admins } = await supabase
          .from("user_roles")
          .select("user_id, profiles!inner(email, full_name)")
          .eq("role", "admin");
        const origin = typeof window !== "undefined" ? window.location.origin : "https://smart-canvas.com";
        for (const a of ((admins ?? []) as any[])) {
          const email = a.profiles?.email;
          if (!email) continue;
          const firstName = ((a.profiles?.full_name as string | null) ?? "").split(/\s+/)[0] || null;
          await sendTxEmail(
            "quote-admin-accept",
            "quote-admin-accept",
            email,
            `quote-admin-accept-${id}`,
            {
              language: lang,
              recipientName: firstName,
              clientName: c?.company_name ?? null,
              quoteTitle: (quoteSnapshot as any).title ?? null,
              quoteNumber: (quoteSnapshot as any).quote_number ?? null,
              sentAt: new Date().toISOString().slice(0, 10),
              ctaUrl: `${origin}/os/admin/quotes/${id}`,
            },
          );
        }
      } catch (err) {
        console.warn("[email] quote-admin-accept failed", err);
      }
    }
  },

  async deleteQuote(id) {
    // FK cascades handle quote_resources, quote_team, quote_sources, etc.
    const { error } = await supabase.from("quotes").delete().eq("id", id);
    if (error) throw error;
  },

  async getQuoteDetail(id) {
    const [{ data: q }, { data: qr }, { data: qt }] = await Promise.all([
      supabase.from("quotes")
        .select("*, clients(company_name, contact_name, contact_email, preferred_language)")
        .eq("id", id).maybeSingle(),
      supabase.from("quote_resources").select("*").eq("quote_id", id).order("order_index"),
      supabase.from("quote_team")
        .select("*, team_profiles(id, display_name, headline, avatar_url, expertise)")
        .eq("quote_id", id).order("order_index"),
    ]);
    return { quote: (q as any) ?? null, resources: (qr ?? []) as any, team: (qt ?? []) as any };
  },

  async listPayments() {
    const { data } = await supabase.from("payments")
      .select("*, invoices(invoice_number, client_id, clients(company_name))")
      .order("paid_at", { ascending: false });
    const flags = await fetchClientFlags();
    const { getDataFilter, isInYear } = await import("@/os/settings/useDataFilterStore");
    const { showSimulated, activeYear } = getDataFilter();
    const sim = new Set(flags.filter((c) => c.is_simulated).map((c) => c.id));
    let rows = (data ?? []) as any[];
    if (!showSimulated) {
      rows = rows.filter((p) => {
        const cid = p.invoices?.client_id;
        return !cid || !sim.has(cid);
      });
    }
    if (activeYear !== "all") {
      rows = rows.filter((p) => isInYear(p.paid_at, activeYear) || isInYear(p.created_at, activeYear));
    }
    return rows as any;
  },
  async createPayment(p) {
    const { error } = await supabase.from("payments").insert(p as any);
    if (error) throw error;
  },

  async listUsersWithRoles() {
    const [{ data: profiles }, { data: roles }, { data: invitees }, { data: clientOwners }] = await Promise.all([
      supabase.from("profiles")
        .select("id, email, full_name, company, position, country, avatar_url, created_at")
        .is("deleted_at", null)
        .order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id, role"),
      supabase.from("project_invitations").select("user_id").not("user_id", "is", null),
      supabase.from("clients").select("user_id").not("user_id", "is", null),
    ]);
    const byUser: Record<string, string[]> = {};
    (roles ?? []).forEach((r: any) => { (byUser[r.user_id] ||= []).push(r.role); });
    const inviteeIds = new Set<string>((invitees ?? []).map((i: any) => i.user_id));
    const clientOwnerIds = new Set<string>((clientOwners ?? []).map((c: any) => c.user_id));
    return (profiles ?? []).map((p: any) => ({
      ...p,
      roles: byUser[p.id] ?? [],
      // Project invitee = present in project_invitations but NOT a client account owner.
      is_project_invitee: inviteeIds.has(p.id) && !clientOwnerIds.has(p.id),
    })) as any;
  },
  async addUserRole(uid, role) {
    const { error } = await supabase.from("user_roles").insert({ user_id: uid, role: role as any });
    if (error) throw error;
  },
  async removeUserRole(uid, role) {
    const { error } = await supabase.from("user_roles").delete().eq("user_id", uid).eq("role", role as any);
    if (error) throw error;
  },

  async clientHubData(userId) {
    const { data: cl } = await supabase.from("clients").select("id").eq("user_id", userId);
    const ids = (cl ?? []).map((c: any) => c.id);
    if (ids.length === 0) return { projects: [], invoices: [] };
    const [{ data: projects }, { data: invoices }] = await Promise.all([
      supabase
        .from("projects")
        .select("id,name,description,status,progress,due_date,start_date,created_at,updated_at,currency,budget,client_id,clients(company_name)")
        .in("client_id", ids),
      supabase.from("invoices").select("id,client_id,invoice_number,amount,currency,status,issue_date,due_date,paid_at,created_at").in("client_id", ids).order("due_date"),
    ]);
    const projIds = (projects ?? []).map((p: any) => p.id);
    if (projIds.length === 0) return { projects: [], invoices: (invoices ?? []) as any };
    const [{ data: ms }, { data: ts }, { data: ups }, teamBy] = await Promise.all([
      supabase.from("milestones").select("project_id,status").in("project_id", projIds),
      supabase.from("tasks").select("project_id,status").in("project_id", projIds),
      supabase.from("project_updates").select("project_id,created_at").in("project_id", projIds),
      fetchTeamMembersByProject(projIds),
    ]);
    const msBy: Record<string, any[]> = {};
    (ms ?? []).forEach((m: any) => (msBy[m.project_id] ||= []).push(m));
    const tsBy: Record<string, any[]> = {};
    (ts ?? []).forEach((t: any) => (tsBy[t.project_id] ||= []).push(t));
    const upBy: Record<string, string[]> = {};
    (ups ?? []).forEach((u: any) => (upBy[u.project_id] ||= []).push(u.created_at));
    const enriched = (projects ?? []).map((p: any) => {
      const milestones = msBy[p.id] ?? [];
      const tasks = tsBy[p.id] ?? [];
      const updates = (upBy[p.id] ?? []).sort();
      const team = teamBy.get(p.id) ?? [];
      return {
        ...p,
        team_count: team.length,
        team_members: team,
        open_tasks: tasks.filter((t: any) => t.status !== "done" && t.status !== "cancelled").length,
        milestones_total: milestones.length,
        milestones_done: milestones.filter((m: any) => m.status === "completed" || m.status === "done").length,
        last_update: updates.length ? updates[updates.length - 1] : (p.updated_at ?? null),
      };
    });
    return { projects: enriched as any, invoices: (invoices ?? []) as any };
  },
  async clientInvoices(userId) {
    const { data: cl } = await supabase.from("clients").select("id").eq("user_id", userId);
    const ids = (cl ?? []).map((c: any) => c.id);
    if (ids.length === 0) return [];
    const { data } = await supabase.from("invoices").select("*").in("client_id", ids).order("created_at", { ascending: false });
    return (data ?? []) as any;
  },
  async clientProjectDetail(id) {
    const [{ data: project }, { data: milestones }, { data: tasks }, { data: updates }] = await Promise.all([
      supabase.from("projects")
        .select("id,name,description,status,progress,budget,currency,client_id,start_date,due_date,created_at,resources_discount_percent_snapshot,billing,clients(company_name)")
        .eq("id", id)
        .maybeSingle(),
      supabase.from("milestones").select("*").eq("project_id", id).order("order_index"),
      supabase.from("tasks").select("*").eq("project_id", id),
      supabase.from("project_updates").select("*").eq("project_id", id).order("created_at", { ascending: false }),
    ]);
    let quoteSummary: string | null = null;
    const clientId = (project as any)?.client_id;
    if (clientId) {
      const { data: q } = await supabase
        .from("quotes")
        .select("description, ai_payload")
        .eq("client_id", clientId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (q) {
        quoteSummary = (q as any).description
          ?? (q as any).ai_payload?.summary
          ?? (q as any).ai_payload?.executive_summary
          ?? null;
      }
    }
    return {
      project: project as any,
      milestones: (milestones ?? []) as any,
      tasks: (tasks ?? []) as any,
      updates: (updates ?? []) as any,
      quoteSummary,
    };
  },

  async operatorAssignedProjects(userId) {
    const { data: mb } = await supabase.from("project_members")
      .select("project_id")
      .eq("user_id", userId);
    const ids = (mb ?? []).map((m: any) => m.project_id);
    if (ids.length === 0) return [] as any;
    const [{ data: projects }, { data: ms }, { data: ts }, { data: ups }] = await Promise.all([
      supabase.from("projects")
        .select("id,name,description,status,progress,budget,currency,client_id,start_date,due_date,created_at,clients(company_name)")
        .in("id", ids)
        .order("created_at", { ascending: false }),
      supabase.from("milestones").select("project_id,status").in("project_id", ids),
      supabase.from("tasks").select("project_id,status").in("project_id", ids),
      supabase.from("project_updates").select("project_id,created_at").in("project_id", ids),
    ]);
    const msBy: Record<string, any[]> = {};
    (ms ?? []).forEach((m: any) => (msBy[m.project_id] ||= []).push(m));
    const tsBy: Record<string, any[]> = {};
    (ts ?? []).forEach((t: any) => (tsBy[t.project_id] ||= []).push(t));
    const upBy: Record<string, string[]> = {};
    (ups ?? []).forEach((u: any) => (upBy[u.project_id] ||= []).push(u.created_at));
    const teamBy = await fetchTeamMembersByProject((projects ?? []).map((p: any) => p.id));
    return (projects ?? []).map((p: any) => {
      const milestones = msBy[p.id] ?? [];
      const tasks = tsBy[p.id] ?? [];
      const updates = (upBy[p.id] ?? []).sort();
      const team = teamBy.get(p.id) ?? [];
      return {
        ...p,
        team_count: Math.max(1, team.length),
        team_members: team,
        open_tasks: tasks.filter((t: any) => t.status !== "done" && t.status !== "cancelled").length,
        milestones_total: milestones.length,
        milestones_done: milestones.filter((m: any) => m.status === "completed" || m.status === "done").length,
        last_update: updates.length ? updates[updates.length - 1] : null,
      };
    }) as any;
  },

  async adminInsights() {
    const [clients, projects, invoices, quotes] = await Promise.all([
      cloudApi.listClients(),
      cloudApi.listProjects(),
      cloudApi.listInvoices(),
      cloudApi.listQuotes(),
    ]);
    return buildAdminInsights({ clients, projects, invoices, quotes });
  },

  /* ───────── Team profiles ───────── */
  async listTeamProfiles() {
    const [{ data }, { data: assigns }] = await Promise.all([
      supabase.from("team_profiles" as any).select("*").order("display_name"),
      supabase.from("project_team" as any).select("team_profile_id"),
    ]);
    const counts: Record<string, number> = {};
    (assigns ?? []).forEach((a: any) => { counts[a.team_profile_id] = (counts[a.team_profile_id] ?? 0) + 1; });
    return ((data ?? []) as any[]).map((p) => ({ ...p, projects_count: counts[p.id] ?? 0 })) as any;
  },
  async getTeamProfile(id) {
    const { data } = await supabase.from("team_profiles" as any).select("*").eq("id", id).maybeSingle();
    return (data ?? null) as any;
  },
  async createTeamProfile(p) {
    const { data, error } = await supabase.from("team_profiles" as any).insert(p as any).select("id").single();
    if (error) throw error;
    return (data as any).id as string;
  },
  async updateTeamProfile(id, patch) {
    const { error } = await supabase.from("team_profiles" as any).update(patch as any).eq("id", id);
    if (error) throw error;
  },
  async deleteTeamProfile(id) {
    const { error } = await supabase.from("team_profiles" as any).delete().eq("id", id);
    if (error) throw error;
  },
  async listProjectTeam(projectId) {
    const { data } = await supabase
      .from("project_team" as any)
      .select("id, project_id, team_profile_id, role_on_project, order_index, profile:team_profile_id(*)")
      .eq("project_id", projectId)
      .order("order_index");
    return ((data ?? []) as any[]).map((r) => ({ ...r, profile: r.profile })) as any;
  },
  async assignTeamToProject(projectId, teamProfileId, roleOnProject) {
    const [{ data: existing }, { data: profile }, { data: project }] = await Promise.all([
      supabase.from("project_team" as any).select("order_index").eq("project_id", projectId),
      supabase.from("team_profiles" as any).select("resource_id").eq("id", teamProfileId).maybeSingle(),
      supabase.from("projects").select("resources_discount_percent_snapshot").eq("id", projectId).maybeSingle(),
    ]);
    const order_index = (existing ?? []).length;
    // Inherit price tier (resource) and monthly rate from the team profile.
    const resourceId = (profile as any)?.resource_id ?? null;
    let monthly: number | null = null;
    if (resourceId) {
      const { data: res } = await supabase
        .from("resources").select("monthly_rate").eq("id", resourceId).maybeSingle();
      if (res) {
        const { applyResourceDiscount } = await import("./pricing");
        let pct = Number((project as any)?.resources_discount_percent_snapshot ?? NaN);
        if (Number.isNaN(pct)) {
          const { data: s } = await supabase.from("app_settings").select("resources_discount_percent").maybeSingle();
          pct = Number((s as any)?.resources_discount_percent ?? 0);
        }
        monthly = applyResourceDiscount(Number((res as any).monthly_rate ?? 0), pct);
      }
    }
    const { error } = await supabase.from("project_team" as any).insert({
      project_id: projectId,
      team_profile_id: teamProfileId,
      role_on_project: roleOnProject ?? null,
      order_index,
      resource_id: resourceId,
      monthly_rate_snapshot: monthly,
    } as any);
    if (error) throw error;
  },
  async removeTeamFromProject(projectTeamId) {
    const { error } = await supabase.from("project_team" as any).delete().eq("id", projectTeamId);
    if (error) throw error;
  },
  async listProjectClientTeam(projectId) {
    const { data } = await supabase
      .from("projects")
      .select("name, clients(company_name, contact_name, contact_email, contact_phone)")
      .eq("id", projectId)
      .maybeSingle();
    const projectName = (data as any)?.name ?? "";
    const c: any = (data as any)?.clients ?? null;
    const seeded = c?.contact_name
      ? [{
          id: `primary-${projectId}`,
          display_name: c.contact_name as string,
          role: "Point of contact",
          email: (c.contact_email as string | null) ?? null,
          phone: (c.contact_phone as string | null) ?? null,
          avatar_url: null,
          location: null,
          is_primary: true,
        }]
      : [];
    const { data: inv } = await supabase
      .from("project_invitations" as any)
      .select("id, name, email, location, is_primary, status, tab_permissions, invited_at, accepted_at, last_session_at")
      .eq("project_id", projectId)
      .order("invited_at", { ascending: true });
    const invited = (inv ?? []).map((r: any) => ({
      id: `inv-${r.id}`,
      invitation_id: r.id as string,
      display_name: r.name as string,
      role: null,
      email: (r.email as string) ?? null,
      phone: null,
      avatar_url: null,
      location: (r.location as string | null) ?? null,
      is_primary: !!r.is_primary,
      status: r.status as any,
      tab_permissions: r.tab_permissions as any,
      invited_at: r.invited_at as string,
      accepted_at: (r.accepted_at as string | null) ?? null,
      last_session_at: (r.last_session_at as string | null) ?? null,
    }));
    return {
      projectName,
      clientName: (c?.company_name as string | null) ?? null,
      contacts: [...seeded, ...invited],
    };
  },

  /* ───────── Project invitations ───────── */
  async listProjectInvitations(projectId) {
    const { data, error } = await supabase
      .from("project_invitations" as any)
      .select("*")
      .eq("project_id", projectId)
      .order("invited_at", { ascending: true });
    if (error) throw error;
    return (data ?? []) as any;
  },
  async createProjectInvitation(projectId, p) {
    const { data, error } = await supabase
      .from("project_invitations" as any)
      .insert({
        project_id: projectId,
        name: p.name,
        email: p.email.trim().toLowerCase(),
        location: p.location ?? null,
        is_primary: !!p.is_primary,
        tab_permissions: p.tab_permissions as any,
      } as any)
      .select("id")
      .single();
    if (error) throw error;
    const invId = (data as any).id as string;
    // Best-effort: send invitation email
    try {
      const [{ data: proj }, { data: auth }] = await Promise.all([
        supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
        supabase.auth.getUser(),
      ]);
      let inviterName: string | null = null;
      let inviterIsClient = false;
      const uid = auth?.user?.id;
      if (uid) {
        const [{ data: prof }, { data: roles }] = await Promise.all([
          supabase.from("profiles").select("full_name").eq("id", uid).maybeSingle(),
          supabase.from("user_roles").select("role").eq("user_id", uid),
        ]);
        inviterName = (prof as any)?.full_name ?? null;
        const roleSet = new Set(((roles ?? []) as any[]).map((r) => r.role));
        // Treat as "client invite" only when the user has client role and no
        // staff role (admin/operator). Staff inviting acts as Smart Canvas.
        inviterIsClient = roleSet.has("client") && !roleSet.has("admin") && !roleSet.has("operator");
      }
      const origin = typeof window !== "undefined" ? window.location.origin : "https://smart-canvas.com";
      if (inviterIsClient) {
        await sendTxEmail(
          "client-team-invitation",
          "client-team-invitation",
          p.email.trim().toLowerCase(),
          `client-team-invitation-${invId}`,
          {
            inviteeName: p.name?.split(/\s+/)[0] ?? null,
            projectName: (proj as any)?.name ?? "un proyecto",
            inviterName,
            signupUrl: `${origin}/os/login`,
          },
        );
      } else {
        await sendTxEmail("project-invitation", "project-invitation", p.email.trim().toLowerCase(), `project-invitation-${invId}`, {
          inviteeName: p.name?.split(/\s+/)[0] ?? null,
          projectName: (proj as any)?.name ?? "un proyecto",
          inviterName,
          role: p.is_primary ? "Contacto principal" : "Cliente",
          signupUrl: `${origin}/os/login`,
        });
      }
    } catch (err) {
      console.warn("[email] project-invitation send failed", err);
    }
    return invId;
  },
  async updateProjectInvitation(id, patch) {
    const clean: any = { ...patch };
    if (typeof clean.email === "string") clean.email = clean.email.trim().toLowerCase();
    const { error } = await supabase.from("project_invitations" as any).update(clean).eq("id", id);
    if (error) throw error;
  },
  async deleteProjectInvitation(id) {
    const { error } = await supabase.from("project_invitations" as any).delete().eq("id", id);
    if (error) throw error;
  },
  async updateClientContact() {
    throw new Error("Editing seeded client contacts is only supported in sandbox.");
  },
  async deleteClientContact() {
    throw new Error("Deleting seeded client contacts is only supported in sandbox.");
  },

  /* ───────── Resources catalog ───────── */
  async listResources() {
    const { data, error } = await supabase
      .from("resources")
      .select("*")
      .order("order_index", { ascending: true });
    if (error) throw error;
    const rows = (data ?? []) as any[];
    if (rows.length > 0) return rows as any;
    // Client accounts cannot read the resources table directly (internal
    // pricing lives there). Fall back to a security-definer RPC that returns
    // only the safe, non-pricing fields needed to render quote UI/labels.
    const { data: safe, error: safeErr } = await supabase.rpc(
      "list_client_visible_resources" as any,
    );
    if (safeErr) return [] as any;
    return (safe ?? []).map((r: any) => ({
      ...r,
      hourly_rate: 0,
      overtime_hourly_rate: 0,
      monthly_rate: 0,
      // Marks rows whose pricing was intentionally withheld so the UI can
      // hide money instead of rendering a misleading $0.00.
      pricing_hidden: true,
    })) as any;
  },
  async createResource(p) {
    const { data, error } = await supabase
      .from("resources")
      .insert(p as any)
      .select("id")
      .single();
    if (error) throw error;
    return (data as any).id as string;
  },
  async updateResource(id, patch) {
    const { error } = await supabase.from("resources").update(patch as any).eq("id", id);
    if (error) throw error;
  },
  async deleteResource(id) {
    const { error } = await supabase.from("resources").delete().eq("id", id);
    if (error) throw error;
  },

  /* ───────── App settings ───────── */
  async getAppSettings() {
    const { data, error } = await supabase
      .from("app_settings" as any)
      .select("resources_discount_percent, invoice_settings, project_tab_settings, quote_settings, email_settings, studio_settings, updated_at")
      .eq("id", true)
      .maybeSingle();
    if (error) throw error;
    return {
      resources_discount_percent: Number((data as any)?.resources_discount_percent ?? 0),
      invoice_settings: ((data as any)?.invoice_settings ?? {}) as any,
      project_tab_settings: ((data as any)?.project_tab_settings ?? {}) as any,
      quote_settings: ((data as any)?.quote_settings ?? {}) as any,
      email_settings: ((data as any)?.email_settings ?? {}) as any,
      studio_settings: ((data as any)?.studio_settings ?? {}) as any,
      updated_at: (data as any)?.updated_at,
    };
  },
  async updateAppSettings(patch) {
    const { error } = await supabase
      .from("app_settings" as any)
      .upsert({ id: true, ...patch } as any, { onConflict: "id" });
    if (error) throw error;
  },

  /* ───────── Payment methods registry ───────── */
  async listPaymentMethods() {
    const { data, error } = await supabase
      .from("payment_methods" as any)
      .select("*")
      .order("order_index", { ascending: true });
    if (error) throw error;
    return (data ?? []) as any;
  },
  async createPaymentMethod(p) {
    if ((p as any)?.is_default_global) {
      await supabase.from("payment_methods" as any)
        .update({ is_default_global: false } as any)
        .neq("id", "00000000-0000-0000-0000-000000000000");
    }
    const { data, error } = await supabase
      .from("payment_methods" as any)
      .insert(p as any)
      .select("id")
      .single();
    if (error) throw error;
    return (data as any).id as string;
  },
  async updatePaymentMethod(id, patch) {
    if ((patch as any)?.is_default_global) {
      await supabase.from("payment_methods" as any)
        .update({ is_default_global: false } as any)
        .neq("id", id);
    }
    const { error } = await supabase.from("payment_methods" as any).update(patch as any).eq("id", id);
    if (error) throw error;
  },
  async deletePaymentMethod(id) {
    const { error } = await supabase.from("payment_methods" as any).delete().eq("id", id);
    if (error) throw error;
  },

  /* ───────── Project from quote ───────── */
  async createProjectFromQuote(quoteId) {
    const { data: q, error: qe } = await supabase
      .from("quotes")
      .select("id, title, quote_number, client_id, currency, monthly_fee, invoice_day_of_month, payment_due_day, total, project_id, discount_snapshot, selected_budget, ai_payload")
      .eq("id", quoteId).maybeSingle();
    if (qe) throw qe;
    if (!q) throw new Error("Quote not found");
    if ((q as any).project_id) throw new Error("Quote already has a project");

    const { data: s } = await supabase.from("app_settings").select("resources_discount_percent").maybeSingle();
    const snap = Number(
      (q as any)?.discount_snapshot?.percent
        ?? (s as any)?.resources_discount_percent
        ?? 0,
    );

    // Resolve client language (for milestone names + invoice notes).
    let lang: "es" | "en" = "es";
    if ((q as any).client_id) {
      const { data: c } = await supabase
        .from("clients")
        .select("preferred_language")
        .eq("id", (q as any).client_id)
        .maybeSingle();
      if ((c as any)?.preferred_language === "en") lang = "en";
    }
    const {
      PHASE_NAMES, PHASE_DESCRIPTIONS, PHASE_WEEKS, PHASE_TASKS,
      lastDayOfMonthISO, proratedForCurrentMonth,
    } = await import("./projectFromQuoteTemplate");

    const projectPayload: any = {
      name: (q as any).title ?? "Project",
      client_id: (q as any).client_id ?? null,
      currency: (q as any).currency ?? "USD",
      budget: (q as any).total ?? null,
      status: "discovery",
      progress: 0,
      start_date: new Date().toISOString().slice(0, 10),
      resources_discount_percent_snapshot: snap,
      billing: {
        monthly_fee: (q as any).monthly_fee ?? null,
        invoice_day_of_month: (q as any).invoice_day_of_month ?? null,
        payment_due_day: (q as any).payment_due_day ?? null,
        currency: (q as any).currency ?? "USD",
      },
    };
    const { data: newP, error: pe } = await supabase.from("projects").insert(projectPayload).select("id").single();
    if (pe) throw pe;
    const projectId = (newP as any).id as string;

    // Link the quote back to the project so it can't be reused.
    await supabase.from("quotes").update({ project_id: projectId } as any).eq("id", quoteId);

    // Seed team from quote_team using the project's locked-in discount.
    const { data: qt } = await supabase
      .from("quote_team")
      .select("team_profile_id, role_on_project, order_index")
      .eq("quote_id", quoteId)
      .order("order_index");
    let teamUserIds: string[] = [];
    if ((qt ?? []).length) {
      const profileIds = (qt ?? []).map((r: any) => r.team_profile_id);
      const { data: profiles } = await supabase
        .from("team_profiles" as any)
        .select("id, resource_id, user_id")
        .in("id", profileIds);
      const profileMap = new Map<string, string | null>(
        (profiles ?? []).map((p: any) => [p.id, p.resource_id ?? null]),
      );
      teamUserIds = (profiles ?? [])
        .map((p: any) => p.user_id)
        .filter(Boolean) as string[];
      const resourceIds = (profiles ?? []).map((p: any) => p.resource_id).filter(Boolean);
      let rateMap = new Map<string, number>();
      if (resourceIds.length) {
        const { data: res } = await supabase.from("resources").select("id, monthly_rate").in("id", resourceIds);
        (res ?? []).forEach((r: any) => rateMap.set(r.id, Number(r.monthly_rate ?? 0)));
      }
      const { applyResourceDiscount } = await import("./pricing");
      const rows = (qt ?? []).map((r: any, i: number) => {
        const rid = profileMap.get(r.team_profile_id) ?? null;
        const base = rid ? rateMap.get(rid) ?? 0 : 0;
        const monthly = rid ? applyResourceDiscount(base, snap) : 0;
        return {
          project_id: projectId,
          team_profile_id: r.team_profile_id,
          role_on_project: r.role_on_project ?? null,
          order_index: i,
          resource_id: rid,
          monthly_rate_snapshot: monthly,
        };
      });
      await supabase.from("project_team" as any).insert(rows as any);
    }

    // Seed full implementation plan as milestones (one per phase).
    const tier: string = (q as any).selected_budget ?? "balanced";
    const customWeeks: number[] | null = (() => {
      const w = (q as any).ai_payload?.phase_weeks_by_tier?.[tier];
      return Array.isArray(w) && w.length === PHASE_WEEKS.length
        ? w.map((v: any, i: number) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.round(Number(v)) : PHASE_WEEKS[i]))
        : null;
    })();
    const weeks = customWeeks ?? PHASE_WEEKS;
    const startMs = Date.parse(projectPayload.start_date);
    let cum = 0;
    const milestoneRows = PHASE_NAMES[lang].map((name, i) => {
      cum += weeks[i] ?? 2;
      return {
        project_id: projectId,
        title: name,
        description: PHASE_DESCRIPTIONS[lang][i] ?? null,
        status: "pending" as const,
        order_index: i,
        due_date: new Date(startMs + cum * 7 * 86400000).toISOString().slice(0, 10),
      };
    });
    const { data: insertedMs } = await supabase
      .from("milestones")
      .insert(milestoneRows as any)
      .select("id, order_index");
    const msByIdx = new Map<number, string>(
      (insertedMs ?? []).map((m: any) => [m.order_index, m.id]),
    );

    // Seed tasks for the first two milestones, round-robin across team users.
    const taskRows: any[] = [];
    PHASE_TASKS[lang].forEach((phaseTasks, phaseIdx) => {
      const milestoneId = msByIdx.get(phaseIdx) ?? null;
      const phaseStartWeeks = weeks.slice(0, phaseIdx).reduce((a, b) => a + b, 0);
      const phaseLen = weeks[phaseIdx] ?? 2;
      phaseTasks.forEach((t, ti) => {
        const offsetDays = Math.round((phaseStartWeeks * 7) + ((ti + 1) / (phaseTasks.length + 1)) * phaseLen * 7);
        taskRows.push({
          project_id: projectId,
          milestone_id: milestoneId,
          title: t.title,
          description: t.description,
          status: "pending",
          due_date: new Date(startMs + offsetDays * 86400000).toISOString().slice(0, 10),
        });
      });
    });
    let insertedTasks: any[] = [];
    if (taskRows.length) {
      const { data } = await supabase.from("tasks" as any).insert(taskRows as any).select("id");
      insertedTasks = data ?? [];
      if (teamUserIds.length && insertedTasks.length) {
        const assignRows = insertedTasks.map((t: any, i: number) => ({
          task_id: t.id,
          user_id: teamUserIds[i % teamUserIds.length],
        }));
        await supabase.from("task_assignees" as any).insert(assignRows as any);
      }
    }

    // First prorated invoice — due on the last day of the current month.
    const monthly = Number((q as any).monthly_fee ?? 0);
    if (monthly > 0 && (q as any).client_id) {
      const { amount } = proratedForCurrentMonth(monthly);
      const issue = new Date().toISOString().slice(0, 10);
      const due = lastDayOfMonthISO();
      const qn = (q as any).quote_number ?? "";
      const note = lang === "es"
        ? `Factura prorrateada — primer mes (${issue} al ${due}). Generada al crear el proyecto desde la cotización ${qn}.`.trim()
        : `Prorated invoice — first month (${issue} to ${due}). Generated when the project was created from quote ${qn}.`.trim();
      // Try to resolve a payment method (client default → global default).
      let payment_method_id: string | null = null;
      const { data: cli } = await supabase
        .from("clients").select("default_payment_method_id").eq("id", (q as any).client_id).maybeSingle();
      payment_method_id = (cli as any)?.default_payment_method_id ?? null;
      if (!payment_method_id) {
        const { data: pms } = await supabase
          .from("payment_methods" as any).select("id, is_default_global").eq("is_active", true);
        const list = (pms ?? []) as any[];
        payment_method_id = list.find((m) => m.is_default_global)?.id ?? list[0]?.id ?? null;
      }
      const stamp = issue.replace(/-/g, "");
      const invoice_number = `INV-${stamp}-${(qn || projectId.slice(0, 4)).slice(-4)}`;
      await supabase.from("invoices").insert({
        client_id: (q as any).client_id,
        project_id: projectId,
        quote_id: quoteId,
        invoice_number,
        amount,
        currency: (q as any).currency ?? "USD",
        status: "sent",
        issue_date: issue,
        due_date: due,
        notes: note,
        reminder_cadence: "weekly",
        payment_method_id,
        auto_generated: true,
      } as any);
    }

    return projectId;
  },
};